import _ from 'lodash';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';

import type {
  ConsolidatedBindingContext,
  BindingSource,
} from '@packages/systems/dynamo/gateway';
import {asFieldDisplayType} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

export const RANDOM_SLUG = '_random';

/**
 * Returns the sortable sources for the given binding context.
 * @param bindingContext
 * @returns Array of sortable sources
 */
export function getSortableSources(
  bindingContext: ConsolidatedBindingContext
): BindingSource[] {
  const sources = bindingContext.getExpandedBindingSources();

  return sources.filter((source) => {
    return (
      _.includes(SharedConfig.sortableByDirectionFieldTypes, source.type) &&
      source.path.length === 1
    );
  });
}

/**
 * Checks if the given sort property is a random sort.
 * @param property
 */
export function isRandomSort(property: string) {
  return property === RANDOM_SLUG;
}

/**
 * Checks if the query sorts include a random sort
 * @param querySorts
 */
export function hasRandomSort(
  querySorts: Array<{property: string; ascending: boolean}>
) {
  return querySorts.some((sort) => isRandomSort(sort.property));
}

/**
 * Handles the inclusion or exclusion of a random sort option in the list of sortable options
 * based on the given query sorts.
 *
 * @param querySorts The list of sort criteria currently applied in the query.
 * @param sortableOptions The list of property options that are sortable.
 * @return The updated list of sortable property options, potentially including a random sort option.
 */
export function handleRandomSortOption(
  querySorts: Array<{property: string; ascending: boolean}>,
  sortableOptions: SortablePropertyOption[]
): SortablePropertyOption[] {
  const randomOption = [
    {
      name: 'Random order',
      value: RANDOM_SLUG,
      type: 'Random',
      displayType: asFieldDisplayType('Random'),
    },
  ];

  if (querySorts.length === 1) {
    return sortableOptions.concat(randomOption);
  }

  if (hasRandomSort(querySorts)) {
    return randomOption;
  }

  return sortableOptions;
}

export type SortablePropertyOption = {
  name: string;
  value: string;
  type: string;
  displayType: string;
};

/**
 * Convert between a binding source and a sortable property option.
 * @param source Binding source
 * @return Entry converted to a sortable property option
 */
export function bindingSourceToSortablePropertyOption(
  source: BindingSource
): SortablePropertyOption {
  const {label, path, type} = source;
  return {
    name: label,
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    value: path[path.length - 1].slug,
    // CLP beta: after shipping we no longer need the `displayType`
    type,
    displayType: asFieldDisplayType(type),
  };
}

/**
 * Retrieves the text for a sort depending on the binding source type.
 * @param bindingSourceType Type of the binding source
 * @param property Property to sort by
 * @param ascending Is ascending sort direction
 * @returns Human readable description of the sort
 */
export function textForSort({
  bindingSourceType,
  property,
  ascending,
}: {
  bindingSourceType: string | null | undefined;
  property: string;
  ascending: boolean;
}): string {
  return isRandomSort(property)
    ? 'shuffle'
    : getSortDescription(bindingSourceType, ascending);
}

/**
 * Retrieves the text for a sort depending on the binding source type.
 * @param type Binding source type
 * @param ascending Sort direction - asc | desc
 * @returns Sort description
 */
function getSortDescription(
  type: string | null | undefined,
  ascending: boolean
): string {
  switch (type) {
    case 'PlainText': {
      return ascending ? 'Alphabetical (A–Z)' : 'Reverse alphabetical (Z–A)';
    }
    case 'Number': {
      return ascending ? 'Smallest to largest' : 'Largest to smallest';
    }
    case 'Bool': {
      return ascending ? 'Is Off First' : 'Is On First';
    }
    case 'Date': {
      return ascending ? 'Oldest to newest' : 'Newest to oldest';
    }
    default: {
      return '';
    }
  }
}

export function isRawSortObj(
  obj: unknown
): obj is {field: string; direction: string} {
  return Boolean(
    obj &&
    typeof obj === 'object' &&
    'field' in obj &&
    typeof obj.field === 'string' &&
    'direction' in obj &&
    typeof obj.direction === 'string'
  );
}
